<template>
  <div>
    <player-breadcrumb
      :view="$t('player_profile.overview')"
      :player="player"
    >
      <v-row dense>
        <v-spacer />
        <v-col cols="auto">
          <date-selector
            v-model="date"
            :label="$t('picker.date.title')"
          />
        </v-col>
        <v-col cols="auto">
          <layout-selector
            :selected-layout.sync="selectedLayout"
            :layouts.sync="layouts"
            @add-layout="openAddLayoutDialog"
            @edit-layout="openEditLayoutDialog"
          />
        </v-col>
      </v-row>
    </player-breadcrumb>

    <grid-layout
      v-if="selectedLayout"
      :key="selectedLayout.name"
      :layout="selectedLayout.grid"
      :col-num="12"
      :row-height="70"
      :is-draggable="false"
      :is-resizable="false"
      :vertical-compact="true"
      :margin="[10, 10]"
      :use-css-transforms="true"
      @layout-ready="layoutReady = true"
    >
      <grid-item
        v-for="item in selectedLayout.grid"
        :key="item.i"
        :x="item.x"
        :y="item.y"
        :w="item.w"
        :h="item.h"
        :i="item.i"
      >
        <div
          v-if="layoutReady"
          class="fill-height"
        >
          <div
            v-if="item.type === 'table'"
            class="fill-height"
          >
            <v-card class="rounded-lg fill-height">
              <v-card-title>
                <v-row align="center">
                  <v-col>
                    {{ $t("card_title.player_overview") }}
                  </v-col>
                  <v-col
                    cols="auto"
                  >
                    <variable-selector
                      v-if="variables.data"
                      :variables="variables.data"
                      :layout-variables.sync="tableHeaders"
                      :show-aggregation="true"
                      @update="saveLayoutVariables"
                    />
                  </v-col>
                </v-row>
              </v-card-title>
              <v-card-text class="pa-0">
                <v-data-table
                  :headers="layoutVariables"
                  :items="playerStats.data"
                  :loading="playerStats.loading"
                  hide-default-footer
                  :items-per-page="-1"
                  dense
                  fixed-header
                  :height="item.h * 75 - 60"
                >
                  <template v-slot:item="{item: statItem}">
                    <player-stats-table-row
                      :item="statItem"
                      :layout-variables="layoutVariables"
                      :player-stats="playerStats"
                    />
                  </template>
                </v-data-table>
              </v-card-text>
            </v-card>
          </div>
          <div
            v-else-if="item.type === 'chart'"
            class="fill-height"
          >
            <diagnostic-chart
              :chart="item"
              :stats="diagnosticStats.data"
              :player="player.data"
              :height="item.h * 70 + 50"
            />
            <load-chart-individual
              class="fill-height"
              :chart="item"
              :stats="playerStats.data"
              :variables="variables.data"
              :current-date="date"
              :loading="playerStats.loading"
            />
            <performance-graph-individual
              :variables="variables.data"
              :chart="item"
              :stats="playerStats.data.length ? playerStats.data[0].stats : {}"
              :player="player.data"
              :height="String(item.h * 70 - 50)"
            />
            <performance-graph
              class="fill-height"
              :players="playerStats.data"
              :chart="item"
              :stats="playerStats.data.length ? playerStats.data[0].stats : {}"
              :gender="player.data.gender"
            />
          </div>
          <div
            v-else-if="item.type === 'line_chart'"
            class="fill-height"
          >
            <load-trend-chart
              :variable-key="item.key"
              :stats="playerStats.data"
              :title="getVariableName(item.key)"
            />
          </div>
          <div
            v-else-if="item.type === 'card'"
            class="fill-height"
          >
            <stat-card
              :loading="playerStats.loading"
              :title="getVariableName(item.key)"
              :value="calculateAverage(item.key)"
              :max="15000"
              :color="getColorForValue(calculateAverage(item.key), 15000)"
            />
          </div>
        </div>
      </grid-item>
    </grid-layout>
  </div>
</template>

<script>
import { LayoutApiService, PlayerApiService, TeamApiService } from '@/data/services'
import dayjs from 'dayjs'
import { getColorForValue, processMaxValues, splitVariable } from '@/utils/helpers'
import PlayerBreadcrumb from '@/components/players/PlayerBreadcrumb.vue'
import debounce from 'lodash/debounce'
import { DashboardType } from '@/data/models/Dashboard'
import { cloneDeep, orderBy } from 'lodash'
import { EventBus } from '@/plugins/bus'
import VariableSelector from '@/components/performance/VariableSelector.vue'
import { GridItem, GridLayout } from 'vue-grid-layout'
import { VariableApiService } from '@/data/services/VariableApiService'
import LoadChartIndividual from '@/components/training-load/LoadChartIndividual.vue'
import LoadTrendChart from '@/components/charts/implementation/LoadTrendChart.vue'
import StatCard from '@/components/StatCard.vue'
import PerformanceGraphIndividual from '@/components/performance/PerformanceGraphIndividual.vue'
import PerformanceGraph from '@/components/performance/PerformanceGraph.vue'
import LayoutSelector from '@/components/LayoutSelector.vue'
import DiagnosticChart from '@/components/charts/implementation/DiagnosticChart.vue'
import DateSelector from '@/components/DateSelector.vue'
import PlayerStatsTableRow from '@/components/players/PlayerStatsTableRow.vue'
import { dashboardConfig } from '@/data/models/DashboardConfig'

export default {
  name: 'PlayerOverviewView',
  components: {
    PlayerStatsTableRow,
    DateSelector,
    DiagnosticChart,
    LayoutSelector,
    PerformanceGraph,
    PerformanceGraphIndividual,
    StatCard,
    LoadTrendChart,
    LoadChartIndividual,
    VariableSelector,
    PlayerBreadcrumb,
    GridLayout,
    GridItem
  },
  data: () => ({
    player: {
      data: {},
      loading: false
    },
    playerStats: {
      data: [],
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 14
      },
      maxValues: {},
      loading: false
    },
    diagnosticStats: {
      data: {},
      loading: false
    },
    teamStats: {
      data: [],
      loading: false
    },
    variables: {
      data: [],
      loading: false
    },
    date: null,
    datePickerVisible: false,
    selectedLayoutObject: null,
    selectedVariablesObject: null,
    layoutReady: false
  }),
  computed: {
    layouts() {
      return this.$store.getters[this.$storeGetters.APP_LAYOUTS]
        .filter(layout => layout.dashboard === DashboardType.PLAYER_OVERVIEW)
    },
    layoutVariables() {
      return this.selectedLayout ? [{ value: 'date', text: this.$t('table_headers.date') }].concat(orderBy(this.tableHeaders.map(header => {
        return {
          ...header,
          value: header.value + (header.aggregation ? `_${header.aggregation}` : ''),
          text: this.getVariableName(header.value + (header.aggregation ? `_${header.aggregation}` : ''))
        }
      }), ['order'])) : []
    },
    selectedLayout: {
      get() {
        if (this.selectedLayoutObject) {
          return cloneDeep(this.selectedLayoutObject)
        }
        return cloneDeep(this.layouts[0])
      },
      set(value) {
        if (value !== this.selectedLayoutObject) this.layoutReady = false
        this.selectedLayoutObject = value
      }
    },
    tableHeaders: {
      get() {
        if (this.selectedVariablesObject) {
          return this.selectedVariablesObject
        } else if (this.selectedLayout) {
          return this.selectedLayout.variables
        }
        return []
      },
      set(value) {
        this.selectedVariablesObject = value
      }
    },
    dayjs() {
      return dayjs
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    hasLayoutManagement() {
      return this.user && this.user.organisation_settings?.has_layout_management
    }
  },
  watch: {
    'playerStats.pagination.current_page': function (newVal, oldVal) {
      this.fetchPlayerStats()
    },
    date: function (newVal, oldVal) {
      if (dayjs(newVal).isSame(dayjs(oldVal), 'date')) return

      this.fetchPlayerStatsDebounced()
    },
    selectedLayout() {
      this.selectedVariablesObject = null
    }
  },
  created() {
    this.date = dayjs()
    this.fetchPlayerDetails()
    this.fetchPlayerStats()
    this.fetchVariables()
    this.fetchDiagnosticStats()

    EventBus.$on(this.$events.LAYOUT_DELETED, (layout) => {
      this.selectedLayoutObject = this.layouts.filter(l => l.id !== layout.id)[0]
    })

    EventBus.$on(this.$events.LAYOUT_UPDATED, () => {
      this.selectedLayoutObject = null
    })
  },
  methods: {
    getColorForValue,
    async fetchPlayerDetails() {
      try {
        this.player.loading = true

        const resp = await PlayerApiService.getPlayerDetails(this.$route.params.id)
        this.player.data = resp.data

        for (const zone of Object.keys(this.player.data.speed_profile)) {
          this.player.data.speed_profile[zone].lower = (this.player.data.speed_profile[zone].lower * 3.6).toFixed(1)
          this.player.data.speed_profile[zone].upper = (this.player.data.speed_profile[zone].upper * 3.6).toFixed(1)
        }
      } catch (e) {
        // todo
      } finally {
        this.player.loading = false
        await this.fetchTeamStats()
      }
    },
    async fetchPlayerStats() {
      try {
        this.playerStats.loading = true

        const queryParams = {
          page: this.playerStats.pagination.current_page,
          per_page: this.playerStats.pagination.per_page,
          dateTo: dayjs(this.date).format('YYYY-MM-DD')
        }

        const resp = await PlayerApiService.getStats(this.$route.params.id, queryParams)

        this.playerStats.maxValues = processMaxValues(resp.data.map(stat => stat.stats))

        this.playerStats.data = resp.data.map(stat => {
          if (!stat.stats) {
            return stat
          }

          if (stat.stats.SLD) stat.stats.SLD = (stat.stats.SLD / 60).toFixed(2)

          if (stat.stats.FAT) stat.stats.FAT_perc = (((6 - stat.stats.FAT) / 5) * 100).toFixed(0)
          if (stat.stats.GMS) stat.stats.GMS_perc = (((6 - stat.stats.GMS) / 5) * 100).toFixed(0)
          if (stat.stats.MOO) stat.stats.MOO_perc = ((stat.stats.MOO / 5) * 100).toFixed(0)
          if (stat.stats.STL) stat.stats.STL_perc = (((6 - stat.stats.STL) / 5) * 100).toFixed(0)

          return stat
        })
        this.playerStats.pagination = resp.meta
      } catch (e) {
        // todo
      } finally {
        this.playerStats.loading = false
      }
    },
    fetchPlayerStatsDebounced: debounce(function () {
      this.fetchPlayerStats()
    }, 500),
    async fetchTeamStats() {
      if (!this.player.data) return

      this.teamStats.loading = true

      try {
        const teamStats = await TeamApiService.getStats(this.player.data.team_id, dayjs(this.date).toISOString(), dayjs(this.date).toISOString())

        this.teamStats.data = teamStats.map(stat => {
          if (stat.stats.FAT) stat.stats.FAT_perc = (((6 - stat.stats.FAT) / 5) * 100).toFixed(0)
          if (stat.stats.GMS) stat.stats.GMS_perc = (((6 - stat.stats.GMS) / 5) * 100).toFixed(0)
          if (stat.stats.MOO) stat.stats.MOO_perc = ((stat.stats.MOO / 5) * 100).toFixed(0)
          if (stat.stats.STL) stat.stats.STL_perc = (((6 - stat.stats.STL) / 5) * 100).toFixed(0)

          return stat
        })
      } catch (e) {
        this.$store.dispatch('alert', {
          text: this.$t('alert.pulling_team_stats.error'),
          color: 'error'
        })
        throw e
      } finally {
        this.teamStats.loading = false
      }
    },
    async fetchVariables() {
      this.variables.loading = true

      try {
        const result = await VariableApiService.getVariables({
          category: dashboardConfig[DashboardType.PLAYER_OVERVIEW].variable_categories
        })

        this.variables.data = result.data
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.loading_variables.error'),
          color: 'error'
        })

        throw e
      } finally {
        this.variables.loading = false
      }
    },
    saveLayoutVariables: debounce(async function () {
      if (!this.hasLayoutManagement) return

      this.submitting = true

      try {
        const data = {
          variables: this.tableHeaders.map(v => ({
            id: v.id,
            order: v.order,
            aggregation: v.aggregation
          }))
        }

        await LayoutApiService.saveVariables(this.selectedLayout.id, data)
      } catch (e) {
        this.$store.dispatch(this.$storeActions.ALERT, {
          text: this.$t('alert.variables_save.error'),
          color: 'error'
        })
      } finally {
        this.submitting = false
      }
    }, 1000),
    async fetchDiagnosticStats() {
      this.diagnosticStats.loading = true

      try {
        this.diagnosticStats.data = await PlayerApiService.getDiagnosticStats(this.$route.params.id, {})
      } catch (e) {
        this.diagnosticStats.data = []
        throw e
      } finally {
        this.diagnosticStats.loading = false
      }
    },
    openAddLayoutDialog() {
      EventBus.$emit(this.$events.ADD_LAYOUT, {
        dashboard: DashboardType.PLAYER_OVERVIEW
      })
    },
    openEditLayoutDialog() {
      EventBus.$emit(this.$events.EDIT_LAYOUT, {
        dashboard: DashboardType.PLAYER_OVERVIEW,
        selectedLayout: this.selectedLayout
      })
    },
    getVariableName(value) {
      const [varKey, aggregation] = splitVariable(value)

      if (!varKey) {
        return this.$t(`variables.${aggregation}.long`)
      }

      return `${this.$t(`variables.${varKey}`)}: ${this.$t(`variables.${aggregation}.long`)}`
    },
    calculateAverage(key) {
      if (this.playerStats.data.length === 0) return null

      const values = this.playerStats.data.map(stat => stat.stats[key]).filter(v => typeof v === 'number')

      if (values.length === 0) return null

      const avg = values.reduce((a, b) => a + b, 0) / values.length
      return Math.round(avg * 100) / 100
    }
  }
}
</script>
